/**
 * ModificationsSection — Modifications & Concours Impact dossier section.
 *
 * Renders as a Collapsible card (collapsed by default) wrapping the ruled
 * spec-block content. The Collapsible header shows the modification count;
 * the body contains the full dossier content including the add/edit form,
 * ImpactGauge roll-up, disclaimers, and modification rows.
 *
 * Honesty contract (spec E5):
 *   - No score, number, or percentage anywhere.
 *   - Four-rung qualitative ladder only (ImpactGauge).
 *   - createdBy stored but never rendered as text.
 *   - Load-bearing disclaimer always visible above the list.
 *   - Attestation teaser (Soon) always present.
 */

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Collapsible } from '@/components/Collapsible';
import { Field, inputStyle, selectStyle } from '@/components/Field';
import { Heading } from '@/components/Heading';
import { ImpactGauge } from '@/components/ImpactGauge';
import { Pill } from '@/components/Pill';
import { SkeletonList } from '@/components/Skeleton';
import { specLabelStyle, specValueMono } from '@/components/SpecBlock';
import { F } from '@/design/fonts';
import { SPACE, T } from '@/design/tokens';
import {
  useCreateModification,
  useModifications,
  useUpdateModification,
  type NewModification,
  type UpdateModificationInput,
} from '@/hooks/useModifications';
import { deriveConcoursRollup } from '@/lib/concours';
import type { ConcoursImpact, JudgeVisibility, Modification } from '@/types/domain';
import { format } from 'date-fns';
import { Wrench } from 'lucide-react';
import { useId, useState } from 'react';

// Field, inputStyle, selectStyle imported from @/components/Field

// textareaStyle extends the shared inputStyle with vertical resize.
const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  resize: 'vertical',
  minHeight: 72,
};

// ---------------------------------------------------------------------------
// Impact select options
// ---------------------------------------------------------------------------

const IMPACT_OPTIONS: { value: ConcoursImpact; label: string }[] = [
  { value: 'none', label: 'None (original spec)' },
  { value: 'minor', label: 'Light' },
  { value: 'moderate', label: 'Notable' },
  { value: 'major', label: 'Significant' },
];

const VISIBILITY_OPTIONS: { value: JudgeVisibility; label: string }[] = [
  { value: 'hidden', label: 'Hidden (under inspection area)' },
  { value: 'subtle', label: 'Visible but non-obvious' },
  { value: 'obvious', label: 'Prominent (clearly identifiable)' },
];

// ---------------------------------------------------------------------------
// Add / edit form
// ---------------------------------------------------------------------------

function ModificationForm({
  vehicleId,
  editMod,
  onDone,
}: {
  vehicleId: string;
  editMod?: Modification;
  onDone: () => void;
}) {
  const { create, pending: createPending } = useCreateModification();
  const { update, pending: updatePending } = useUpdateModification();
  const pending = createPending || updatePending;

  const [title, setTitle] = useState(editMod?.title ?? '');
  const [category, setCategory] = useState<Modification['category']>(editMod?.category ?? 'other');
  const [description, setDescription] = useState(editMod?.description ?? '');
  const [installedAt, setInstalledAt] = useState(editMod?.installedAt ?? '');
  const [concoursImpact, setConcoursImpact] = useState<ConcoursImpact>(
    editMod?.concoursImpact ?? 'none',
  );
  const [reversible, setReversible] = useState(editMod?.reversible ?? false);
  const [oeRetained, setOeRetained] = useState(editMod?.oeRetained ?? false);
  const [judgeVisibility, setJudgeVisibility] = useState<JudgeVisibility>(
    editMod?.judgeVisibility ?? 'hidden',
  );
  const [restorationNote, setRestorationNote] = useState(editMod?.restorationNote ?? '');
  const [costStr, setCostStr] = useState(
    editMod?.restorationCostMinor !== undefined ? String(editMod.restorationCostMinor) : '',
  );
  const [currency, setCurrency] = useState(editMod?.currency ?? '');
  const [titleError, setTitleError] = useState('');
  const [submitError, setSubmitError] = useState('');

  const baseId = useId();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) {
      setTitleError('Title is required');
      return;
    }
    setTitleError('');
    setSubmitError('');

    // Parse restoration cost as integer minor units. Reject floats.
    let restorationCostMinor: number | undefined;
    if (costStr.trim()) {
      const parsed = parseInt(costStr, 10);
      if (isNaN(parsed) || parsed < 0 || String(parsed) !== costStr.trim()) {
        setSubmitError('Restoration cost must be a whole number (minor units, e.g. cents)');
        return;
      }
      restorationCostMinor = parsed;
    }

    try {
      if (editMod) {
        const input: UpdateModificationInput = {
          title: title.trim(),
          category,
          concoursImpact,
          reversible,
          oeRetained,
          judgeVisibility,
        };
        if (description.trim()) input.description = description.trim();
        if (installedAt) input.installedAt = installedAt;
        if (restorationNote.trim()) input.restorationNote = restorationNote.trim();
        if (restorationCostMinor !== undefined) input.restorationCostMinor = restorationCostMinor;
        if (currency.trim()) input.currency = currency.trim().toUpperCase();
        await update(vehicleId, editMod.id, input);
      } else {
        const input: NewModification = {
          title: title.trim(),
          category,
          concoursImpact,
          reversible,
          oeRetained,
          judgeVisibility,
        };
        if (description.trim()) input.description = description.trim();
        if (installedAt) input.installedAt = installedAt;
        if (restorationNote.trim()) input.restorationNote = restorationNote.trim();
        if (restorationCostMinor !== undefined) input.restorationCostMinor = restorationCostMinor;
        if (currency.trim()) input.currency = currency.trim().toUpperCase();
        await create(vehicleId, input);
      }
      onDone();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Could not save modification');
    }
  }

  return (
    <form
      onSubmit={(e) => {
        void handleSubmit(e);
      }}
      style={{ display: 'grid', gap: SPACE.md }}
    >
      <Heading level={3} size="md">
        {editMod ? 'Edit modification' : 'Add modification'}
      </Heading>

      <Field label="Title" error={titleError} errorId={`${baseId}-title-error`}>
        <input
          style={inputStyle}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Replaced air filter"
          aria-describedby={titleError ? `${baseId}-title-error` : undefined}
        />
      </Field>

      <Field label="Category (optional)">
        <select
          style={selectStyle}
          value={category}
          onChange={(e) => setCategory(e.target.value as Modification['category'])}
        >
          <option value="other">Other</option>
          <option value="engine">Engine</option>
          <option value="suspension">Suspension</option>
          <option value="brakes">Brakes</option>
          <option value="interior">Interior</option>
          <option value="exterior">Exterior</option>
          <option value="electrical">Electrical</option>
        </select>
      </Field>

      <Field label="Description (optional)">
        <input
          style={inputStyle}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Brief description of the work done"
        />
      </Field>

      <Field label="Date installed (optional)">
        <input
          type="date"
          style={inputStyle}
          value={installedAt}
          onChange={(e) => setInstalledAt(e.target.value)}
        />
      </Field>

      <Field label="Concours impact">
        <select
          style={selectStyle}
          value={concoursImpact}
          onChange={(e) => setConcoursImpact(e.target.value as ConcoursImpact)}
        >
          {IMPACT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </Field>

      <div style={{ display: 'flex', gap: SPACE.md, flexWrap: 'wrap' }}>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: SPACE.sm,
            cursor: 'pointer',
            minHeight: 44,
          }}
        >
          <input
            type="checkbox"
            checked={reversible}
            onChange={(e) => setReversible(e.target.checked)}
          />
          <span style={{ fontSize: 14, color: T.text, fontFamily: F.body }}>
            Reversible (bolt-on)
          </span>
        </label>

        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: SPACE.sm,
            cursor: 'pointer',
            minHeight: 44,
          }}
        >
          <input
            type="checkbox"
            checked={oeRetained}
            onChange={(e) => setOeRetained(e.target.checked)}
          />
          <span style={{ fontSize: 14, color: T.text, fontFamily: F.body }}>
            Original parts kept
          </span>
        </label>
      </div>

      <Field label="Visible to judges">
        <select
          style={selectStyle}
          value={judgeVisibility}
          onChange={(e) => setJudgeVisibility(e.target.value as JudgeVisibility)}
        >
          {VISIBILITY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Back-to-stock note (optional)">
        <textarea
          style={textareaStyle}
          value={restorationNote}
          onChange={(e) => setRestorationNote(e.target.value)}
          placeholder="What it would take to return to factory spec"
        />
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: SPACE.sm }}>
        <Field label="Restoration cost (optional, minor units e.g. cents)">
          <input
            type="number"
            inputMode="numeric"
            min={0}
            step={1}
            style={inputStyle}
            value={costStr}
            onChange={(e) => setCostStr(e.target.value)}
            placeholder="e.g. 35000 for $350.00"
          />
        </Field>
        <Field label="Currency (ISO-4217)">
          <input
            style={{ ...inputStyle, width: 80 }}
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            placeholder="USD"
            maxLength={3}
          />
        </Field>
      </div>

      {submitError ? (
        <p role="alert" style={{ color: T.red, fontSize: 13, margin: 0 }}>
          {submitError}
        </p>
      ) : null}

      <div style={{ display: 'flex', gap: SPACE.md }}>
        <Button type="submit" size="sm" loading={pending} disabled={!title.trim()}>
          {editMod ? 'Save' : 'Add modification'}
        </Button>
        <Button type="button" size="sm" variant="secondary" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Per-modification row
// ---------------------------------------------------------------------------

/** Mono label/value pair, dossier-style.
 * Borrows specLabelStyle/specValueMono; ModificationsSection is a ruled list, not a card-boxed SpecBlock.
 */
function AttrRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: SPACE.sm,
        alignItems: 'baseline',
        flexWrap: 'wrap',
        marginTop: SPACE.xs,
      }}
    >
      <span style={{ ...specLabelStyle, whiteSpace: 'nowrap' }}>{label}</span>
      <span style={{ ...specValueMono, fontSize: 12 }}>{value}</span>
    </div>
  );
}

function oeRetainedLabel(oeRetained: boolean): string {
  return oeRetained ? 'Kept' : 'Not kept';
}

function reversibleLabel(reversible: boolean): string {
  // Two-way boolean mapping per spec. The spec acknowledges there is no
  // "With effort" / "Partly" rung in the boolean model.
  return reversible ? 'Yes (bolt-on)' : 'No (permanent)';
}

function visibilityLabel(v: JudgeVisibility): string {
  switch (v) {
    case 'hidden':
      return 'Hidden';
    case 'subtle':
      return 'Visible';
    case 'obvious':
      return 'Prominent';
  }
}

function formatMinorCost(minor: number, currency?: string): string {
  // Display as major units (divide by 100) with 2 decimal places.
  const major = (minor / 100).toFixed(2);
  return currency ? `${currency} ${major}` : major;
}

function ModificationRow({
  mod,
  isLast,
  onEdit,
}: {
  mod: Modification;
  isLast: boolean;
  onEdit: (m: Modification) => void;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: `${SPACE.sm}px ${SPACE.md}px`,
        alignItems: 'start',
        padding: `${SPACE.md}px 0`,
        borderBottom: isLast ? 'none' : `1px solid ${T.border}`,
      }}
    >
      {/* Left: title + meta */}
      <div>
        <div
          style={{
            fontFamily: F.body,
            fontWeight: 600,
            fontSize: 14,
            color: T.text,
          }}
        >
          {mod.title}
        </div>
        {mod.description ? (
          <div
            style={{
              fontFamily: F.body,
              fontSize: 13,
              color: T.textMuted,
              marginTop: SPACE.xs,
              lineHeight: 1.5,
            }}
          >
            {mod.description}
          </div>
        ) : null}
        {mod.installedAt ? (
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: SPACE.xs }}>
            {format(new Date(mod.installedAt), 'MMM yyyy')}
          </div>
        ) : null}

        {/* Attribute fact rows — mono label/value pairs */}
        <AttrRow label="Original parts" value={oeRetainedLabel(mod.oeRetained)} />
        <AttrRow label="Reversible" value={reversibleLabel(mod.reversible)} />
        <AttrRow label="Visible to judges" value={visibilityLabel(mod.judgeVisibility)} />
        {mod.restorationNote ? (
          <AttrRow label="Back-to-stock note" value={mod.restorationNote} />
        ) : null}
        {typeof mod.restorationCostMinor === 'number' ? (
          <AttrRow
            label="Restoration cost"
            value={formatMinorCost(mod.restorationCostMinor, mod.currency)}
          />
        ) : null}

        <div style={{ marginTop: SPACE.sm }}>
          <Button size="sm" variant="ghost" onClick={() => onEdit(mod)}>
            Edit
          </Button>
        </div>
      </div>

      {/* Right: per-modification gauge */}
      <div style={{ paddingTop: 2 }}>
        <ImpactGauge impact={mod.concoursImpact} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main exported section
// ---------------------------------------------------------------------------

export function ModificationsSection({ vehicleId }: { vehicleId: string }) {
  const { data: mods, loading } = useModifications(vehicleId);
  const [showForm, setShowForm] = useState(false);
  const [editMod, setEditMod] = useState<Modification | undefined>(undefined);

  const rollup = deriveConcoursRollup(mods);
  const isEmpty = mods.length === 0;

  function handleEdit(mod: Modification) {
    setEditMod(mod);
    setShowForm(true);
  }

  function handleAddNew() {
    setEditMod(undefined);
    setShowForm(true);
  }

  function handleFormDone() {
    setShowForm(false);
    setEditMod(undefined);
  }

  return (
    <Collapsible
      title="Modifications & concours impact"
      count={mods.length}
      id="modifications"
      leadingIcon={<Wrench size={14} />}
      headerAccessory={<ImpactGauge impact={rollup.worst} />}
    >
      {/* Lede + roll-up gauge — shown on expand */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: SPACE.md,
          flexWrap: 'wrap',
          marginBottom: SPACE.sm,
        }}
      >
        <p
          style={{
            fontFamily: F.body,
            fontSize: 13,
            color: T.textMuted,
            margin: 0,
            lineHeight: 1.6,
          }}
        >
          Every change from factory spec, logged by the owner, with an honest read on how it affects
          concours eligibility.
        </p>

        {/* Roll-up: worst-case gauge + count pill */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: SPACE.sm,
            flexShrink: 0,
          }}
        >
          <ImpactGauge impact={rollup.worst} />
          <Pill variant="neutral" size="sm">
            {isEmpty ? 'none logged' : `${rollup.count} logged`}
          </Pill>
        </div>
      </div>

      {/* Disclaimer — load-bearing, body text not fine print */}
      <p
        style={{
          fontFamily: F.body,
          fontSize: 13,
          color: T.text,
          lineHeight: 1.6,
          margin: `0 0 ${SPACE.xs}px`,
        }}
      >
        Owner&apos;s own assessment of how these changes affect concours eligibility, not an
        official score from a judging body. An official score comes from a sanctioning body&apos;s
        judging sheet.
      </p>

      {/* Attestation teaser — muted, never pink */}
      <p
        style={{
          fontFamily: F.body,
          fontSize: 13,
          color: T.textMuted,
          margin: `0 0 ${SPACE.md}px`,
          lineHeight: 1.5,
        }}
      >
        A marque specialist can attest to this assessment.{' '}
        <span
          style={{
            display: 'inline-block',
            fontFamily: F.mono,
            fontSize: 10,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: T.textMuted,
            border: `1px solid ${T.border}`,
            borderRadius: 2,
            padding: `1px ${SPACE.xs}px`,
            verticalAlign: 'middle',
          }}
        >
          Soon
        </span>
      </p>

      {/* Inline form */}
      {showForm ? (
        <Card style={{ marginBottom: SPACE.md }}>
          <ModificationForm
            vehicleId={vehicleId}
            {...(editMod !== undefined ? { editMod } : {})}
            onDone={handleFormDone}
          />
        </Card>
      ) : null}

      {/* Content */}
      {loading ? (
        <SkeletonList count={2} />
      ) : isEmpty ? (
        /* Empty state */
        <div
          style={{
            padding: `${SPACE.lg}px 0`,
            borderTop: `1px solid ${T.border}`,
          }}
        >
          <p
            style={{
              fontFamily: F.body,
              fontSize: 13,
              color: T.textMuted,
              margin: `0 0 ${SPACE.sm}px`,
              lineHeight: 1.6,
            }}
          >
            No modifications logged yet. Start by recording the first change from factory spec.
          </p>
          {!showForm ? (
            <Button size="sm" variant="secondary" onClick={handleAddNew}>
              Add modification
            </Button>
          ) : null}
        </div>
      ) : (
        /* Modification rows */
        <div
          style={{
            borderTop: `1px solid ${T.border}`,
          }}
        >
          {mods.map((mod, idx) => (
            <ModificationRow
              key={mod.id}
              mod={mod}
              isLast={idx === mods.length - 1}
              onEdit={handleEdit}
            />
          ))}
          {!showForm ? (
            <div style={{ paddingTop: SPACE.sm }}>
              <Button size="sm" variant="secondary" onClick={handleAddNew}>
                Add modification
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </Collapsible>
  );
}
